import { useEffect, useMemo, useRef, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import {
  Hash, Send, Paperclip, Search, Pin, BellOff, Bell, Smile, Reply,
  Image as ImageIcon, Plus, Users, Building2, FolderKanban, Megaphone,
  HeartHandshake, Wrench, Circle, X, MoreHorizontal, CheckCheck,
  Lock, Globe2, ChevronDown, ChevronRight, ChevronLeft, Video,
} from "lucide-react";

const STATUS_COLOR = { online: "bg-emerald-500", away: "bg-amber-500", busy: "bg-red-500", offline: "bg-slate-300" };
const REACTIONS = ["👍", "❤️", "😂", "🎉", "👀", "🙏"];

function typeIcon(t, c) {
  if (c?.type === "announcement") return <Megaphone className="w-4 h-4 text-cyan-500" />;
  if (c?.icon === "building" || t === "department") return <Building2 className="w-4 h-4 text-indigo-500" />;
  if (c?.icon === "folder" || t === "project") return <FolderKanban className="w-4 h-4 text-amber-500" />;
  if (t === "support" && c?.support_team === "hr_admin") return <HeartHandshake className="w-4 h-4 text-rose-500" />;
  if (t === "support") return <Wrench className="w-4 h-4 text-slate-600" />;
  if (t === "private") return <Lock className="w-4 h-4 text-slate-500" />;
  if (t === "dm") return null;
  return <Hash className="w-4 h-4 text-slate-500" />;
}

function UserAvatar({ emp, size = 32, presence }) {
  const initials = (emp?.full_name || "U").split(" ").map(n => n[0]).slice(0, 2).join("");
  return (
    <div className="relative shrink-0" style={{ width: size, height: size }}>
      <Avatar style={{ width: size, height: size }}>
        {emp?.avatar_url && <AvatarImage src={emp.avatar_url} alt={emp.full_name} />}
        <AvatarFallback className="bg-slate-800 text-white text-[11px]">{initials}</AvatarFallback>
      </Avatar>
      {presence && <span className={`absolute bottom-0 right-0 w-2.5 h-2.5 rounded-full border-2 border-white ${STATUS_COLOR[presence] || STATUS_COLOR.offline}`} />}
    </div>
  );
}

function formatTime(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const now = new Date();
  const sameDay = d.toDateString() === now.toDateString();
  const yest = new Date(now); yest.setDate(now.getDate() - 1);
  const isYest = d.toDateString() === yest.toDateString();
  if (sameDay) return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  if (isYest) return "Yesterday";
  if ((now - d) / 86400000 < 7) return d.toLocaleDateString([], { weekday: "short" });
  return d.toLocaleDateString();
}

function dayLabel(iso) {
  const d = new Date(iso); const now = new Date();
  const sameDay = d.toDateString() === now.toDateString();
  const yest = new Date(now); yest.setDate(now.getDate() - 1);
  if (sameDay) return "Today";
  if (d.toDateString() === yest.toDateString()) return "Yesterday";
  return d.toLocaleDateString(undefined, { weekday: "long", month: "short", day: "numeric" });
}

// ============ SECTION LIST ============
const SECTIONS = [
  { key: "pinned",        label: "Pinned",            filter: (c) => c.pinned },
  { key: "dm",            label: "Direct Messages",   filter: (c) => c.type === "dm" },
  { key: "department",    label: "Departments",       filter: (c) => c.type === "department" },
  { key: "project",       label: "Projects",          filter: (c) => c.type === "project" },
  { key: "support",       label: "Help & Support",    filter: (c) => c.type === "support" },
  { key: "announcement",  label: "Announcements",     filter: (c) => c.type === "announcement" },
  { key: "channels",      label: "Channels",          filter: (c) => c.type === "public" || c.type === "private" },
];

function ChannelRow({ ch, active, onClick, presence, emps }) {
  const isDM = ch.type === "dm";
  const dmPeer = isDM ? emps.find(e => e.id !== null && (ch.members || []).find(m => m !== (ch.__myId || ""))) : null;
  // use non-self member
  const peerId = isDM ? (ch.members || []).find(m => m !== ch.__myId) : null;
  const peer = isDM ? emps.find(e => e.id === peerId) : null;
  const peerPresence = peer ? (presence[peer.user_id || peer.id]?.status || "offline") : null;
  const label = isDM ? (peer?.full_name || ch.name) : ch.name;
  const preview = ch.last_message?.text || (ch.last_message?.attachments?.length ? "📎 Attachment" : (ch.description || ""));
  return (
    <button
      onClick={onClick}
      className={`w-full text-left px-3 py-2.5 rounded-xl flex items-center gap-3 group transition shadow-none ${
        active
          ? "qh-grad-bg text-white shadow-md shadow-violet-900/20"
          : "hover:bg-slate-100 text-slate-800"
      }`}
      data-testid={`chan-row-${ch.id}`}
    >
      {isDM ? (
        <UserAvatar emp={peer} size={36} presence={peerPresence} />
      ) : (
        <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${active ? "bg-white/10" : "bg-slate-100"}`}>{typeIcon(ch.type, ch)}</div>
      )}
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between">
          <div className={`text-sm font-medium truncate ${active ? "text-white" : "text-slate-900"}`}>{label}</div>
          <div className={`text-[10px] shrink-0 ml-1 ${active ? "text-white/60" : "text-slate-400"}`}>{formatTime(ch.last_message?.created_at)}</div>
        </div>
        <div className="flex items-center justify-between gap-2">
          <div className={`text-[11px] truncate ${active ? "text-white/70" : "text-slate-500"}`}>{preview || (isDM ? "Start a conversation" : " ")}</div>
          <div className="flex items-center gap-1">
            {ch.pinned && <Pin className={`w-3 h-3 ${active ? "text-white/70" : "text-slate-400"}`} />}
            {ch.muted && <BellOff className={`w-3 h-3 ${active ? "text-white/70" : "text-slate-400"}`} />}
            {ch.unread > 0 && <Badge className={`h-4 min-w-4 px-1 text-[10px] ${active ? "bg-white text-slate-900" : "bg-cyan-500 text-white"}`}>{ch.unread}</Badge>}
          </div>
        </div>
      </div>
    </button>
  );
}

// ============ MESSAGE BUBBLE ============
function MessageBubble({ m, mine, onReact, onReply, onDelete, compact, sender, prevSender }) {
  const showHeader = !compact || prevSender !== m.sender_id;
  const hasReactions = m.reactions && Object.keys(m.reactions).length > 0;

  return (
    <div className={`group flex gap-2 ${mine ? "flex-row-reverse" : ""} ${showHeader ? "mt-3" : "mt-0.5"}`} data-testid={`msg-${m.id}`}>
      {showHeader ? <UserAvatar emp={sender} size={32} /> : <div className="w-8 shrink-0" />}
      <div className={`max-w-[85%] sm:max-w-[72%] ${mine ? "items-end" : "items-start"} flex flex-col`}>
        {showHeader && (
          <div className={`flex items-center gap-2 px-1 mb-0.5 ${mine ? "flex-row-reverse" : ""}`}>
            <span className="text-xs font-semibold text-slate-700">{sender?.full_name || "Unknown"}</span>
            <span className="text-[10px] text-slate-400">{formatTime(m.created_at)}</span>
          </div>
        )}
        {m.reply_preview && (
          <div className={`mb-1 border-l-2 ${mine ? "border-cyan-300" : "border-slate-300"} pl-2 py-0.5 max-w-full rounded bg-slate-50/60`}>
            <div className="text-[10px] font-semibold text-slate-500">{m.reply_preview.sender_name || "Reply"}</div>
            <div className="text-[11px] text-slate-600 truncate">{m.reply_preview.text}</div>
          </div>
        )}
        <div className="flex items-center gap-1 relative">
          <div className={`${mine ? "bg-cyan-600 text-white" : "bg-white text-slate-900 border border-slate-200"} px-3 py-2 rounded-2xl text-sm whitespace-pre-wrap break-words shadow-sm`}>
            {m.deleted ? <span className="italic text-slate-400">This message was deleted</span> : (m.text || "")}
            {m.attachments_detail && m.attachments_detail.length > 0 && (
              <div className="mt-2 space-y-1">
                {m.attachments_detail.map((a) => (
                  <a key={a?.id} href={api.defaults.baseURL + `/files/${a?.id}/download`} target="_blank" rel="noreferrer" className={`flex items-center gap-2 text-xs px-2 py-1 rounded ${mine ? "bg-white/15 text-white" : "bg-slate-50 border border-slate-200 text-slate-700"}`}>
                    <Paperclip className="w-3 h-3" /> {a?.name || "attachment"}
                  </a>
                ))}
              </div>
            )}
          </div>
          {/* hover actions */}
          {!m.deleted && (
            <div className={`absolute top-0 ${mine ? "right-full mr-1" : "left-full ml-1"} opacity-0 group-hover:opacity-100 transition flex items-center gap-0.5 bg-white border border-slate-200 rounded-full shadow-sm px-1 py-0.5`}>
              <Popover>
                <PopoverTrigger asChild><button className="p-1 hover:bg-slate-100 rounded-full" title="React" data-testid={`react-${m.id}`}><Smile className="w-3.5 h-3.5 text-slate-600" /></button></PopoverTrigger>
                <PopoverContent className="p-1 w-auto" side="top" align="center">
                  <div className="flex items-center gap-1">
                    {REACTIONS.map(e => <button key={e} className="px-1.5 py-1 hover:bg-slate-100 rounded text-lg" onClick={() => onReact(m.id, e)}>{e}</button>)}
                  </div>
                </PopoverContent>
              </Popover>
              <button className="p-1 hover:bg-slate-100 rounded-full" title="Reply" onClick={() => onReply(m)} data-testid={`reply-${m.id}`}><Reply className="w-3.5 h-3.5 text-slate-600" /></button>
              {mine && <button className="p-1 hover:bg-red-50 rounded-full" title="Delete" onClick={() => onDelete(m.id)} data-testid={`msg-delete-${m.id}`}><X className="w-3.5 h-3.5 text-red-500" /></button>}
            </div>
          )}
        </div>
        {hasReactions && (
          <div className={`flex flex-wrap gap-1 mt-1 ${mine ? "justify-end" : ""}`}>
            {Object.entries(m.reactions).map(([emoji, users]) => (
              <button key={emoji} onClick={() => onReact(m.id, emoji)} className="px-2 py-0.5 bg-white border border-slate-200 rounded-full text-xs flex items-center gap-1 hover:bg-slate-50">
                <span>{emoji}</span><span className="text-slate-600">{users.length}</span>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ============ MAIN ============
export default function Messaging() {
  const { user } = useAuth();
  const [channels, setChannels] = useState([]);
  const [emps, setEmps] = useState([]);
  const [presence, setPresence] = useState({});
  const [active, setActive] = useState(null);
  const [msgs, setMsgs] = useState([]);
  const [text, setText] = useState("");
  const [replyTo, setReplyTo] = useState(null);
  const [search, setSearch] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [showSearch, setShowSearch] = useState(false);
  const [collapsed, setCollapsed] = useState({}); // sectionKey -> collapsed
  const [showDetails, setShowDetails] = useState(false);
  const [newChannelOpen, setNewChannelOpen] = useState(false);
  const [newForm, setNewForm] = useState({ name: "", description: "", type: "public", members: [] });
  const [memberMgrOpen, setMemberMgrOpen] = useState(false);
  const [settings, setSettings] = useState(null);
  const [newDmOpen, setNewDmOpen] = useState(false);
  const listRef = useRef(null);

  const myEmpId = user?.employee_id;

  // Load channels + employees + settings
  const loadAll = async () => {
    const [c, e, s] = await Promise.all([
      api.get("/v2/channels"),
      api.get("/employees"),
      api.get("/messaging/settings").catch(() => ({ data: null })),
    ]);
    const mapped = c.data.map(x => ({ ...x, __myId: myEmpId }));
    setChannels(mapped); setEmps(e.data); setSettings(s.data);
    if (!active && mapped.length) setActive(mapped.find(m => m.type === "public") || mapped[0]);
  };
  useEffect(() => { loadAll(); // eslint-disable-next-line
  }, []);

  // Presence
  useEffect(() => {
    const beat = () => api.post("/presence/heartbeat", { status: "online" }).catch(() => {});
    beat();
    const load = () => api.get("/presence").then(r => setPresence(r.data)).catch(() => {});
    load();
    const t1 = setInterval(beat, 60_000);
    const t2 = setInterval(load, 30_000);
    return () => { clearInterval(t1); clearInterval(t2); };
  }, []);

  // Load messages when active changes
  const loadMsgs = async (cid) => {
    if (!cid) return;
    const r = await api.get(`/v2/channels/${cid}/messages`);
    setMsgs(r.data);
    // refresh unread counts
    setChannels(prev => prev.map(p => p.id === cid ? { ...p, unread: 0 } : p));
    setTimeout(() => listRef.current?.scrollTo(0, listRef.current.scrollHeight), 50);
  };
  useEffect(() => { if (active) loadMsgs(active.id); // eslint-disable-next-line
  }, [active?.id]);

  // Poll messages every 6s when a channel is active
  useEffect(() => {
    if (!active) return;
    const t = setInterval(async () => {
      try {
        const r = await api.get(`/v2/channels/${active.id}/messages`);
        setMsgs(prev => {
          if (prev.length !== r.data.length) return r.data;
          if (prev.length && r.data.length && prev[prev.length - 1].id !== r.data[r.data.length - 1].id) return r.data;
          return prev;
        });
      } catch {}
    }, 6000);
    return () => clearInterval(t);
  }, [active?.id]);

  // Poll channel list every 20s for unread badge refresh
  useEffect(() => {
    const t = setInterval(() => {
      api.get("/v2/channels").then(r => {
        setChannels(r.data.map(x => ({ ...x, __myId: myEmpId })));
      }).catch(() => {});
    }, 20000);
    return () => clearInterval(t); // eslint-disable-next-line
  }, [myEmpId]);

  const send = async () => {
    if (!(text.trim()) || !active) return;
    try {
      await api.post(`/v2/channels/${active.id}/messages`, { text: text.trim(), reply_to: replyTo?.id || null });
      setText(""); setReplyTo(null);
      await loadMsgs(active.id);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const reactTo = async (mid, emoji) => {
    await api.post(`/v2/messages/${mid}/reactions`, { emoji });
    loadMsgs(active.id);
  };
  const deleteMsg = async (mid) => {
    if (!window.confirm("Delete this message?")) return;
    await api.delete(`/v2/messages/${mid}`);
    loadMsgs(active.id);
  };
  const togglePin = async () => {
    if (!active) return;
    await api.patch(`/v2/channels/${active.id}/prefs`, { pinned: !active.pinned });
    loadAll();
  };
  const toggleMute = async () => {
    if (!active) return;
    await api.patch(`/v2/channels/${active.id}/prefs`, { muted: !active.muted });
    loadAll();
  };

  const startDM = async (empId) => {
    try {
      const r = await api.post(`/v2/channels/dm/${empId}`);
      await loadAll();
      setActive({ ...r.data, __myId: myEmpId });
      setNewDmOpen(false);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const createChannel = async () => {
    try {
      const r = await api.post("/v2/channels", newForm);
      toast.success(`Channel #${r.data.name} created`);
      setNewChannelOpen(false); setNewForm({ name: "", description: "", type: "public", members: [] });
      await loadAll();
      setActive({ ...r.data, __myId: myEmpId });
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const updateMembers = async (add, remove) => {
    if (!active) return;
    try {
      await api.patch(`/v2/channels/${active.id}/members`, { add, remove });
      await loadAll();
      toast.success("Members updated");
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const runSearch = async (q) => {
    if (!q || q.length < 2) { setSearchResults([]); return; }
    const r = await api.get(`/v2/messaging/search?q=${encodeURIComponent(q)}`);
    setSearchResults(r.data);
  };

  // Group channels into sections
  const sidebarSections = useMemo(() => {
    const filtered = channels.filter(c => {
      if (!search) return true;
      const q = search.toLowerCase();
      return (c.name || "").toLowerCase().includes(q) || (c.description || "").toLowerCase().includes(q);
    });
    return SECTIONS.map(s => ({ ...s, items: filtered.filter(s.filter).filter(c => !(s.key !== "pinned" && c.pinned)) || [] })).filter(s => s.items.length || s.key === "dm" || s.key === "pinned");
  }, [channels, search]);

  // Group messages by day for headers
  const msgsWithDays = useMemo(() => {
    const out = [];
    let lastDay = null;
    for (const m of msgs) {
      const d = (m.created_at || "").slice(0, 10);
      if (d && d !== lastDay) { out.push({ __day: m.created_at }); lastDay = d; }
      out.push(m);
    }
    return out;
  }, [msgs]);

  const activeMembers = active ? emps.filter(e => (active.members || []).includes(e.id)) : [];
  const canEditMembers = active && !active.auto_managed && active.type !== "dm" && (user?.role === "super_admin" || user?.role === "hr_admin" || active.members?.includes(myEmpId));

  return (
    <div className="h-[calc(100vh-56px)] flex bg-gradient-to-br from-slate-50 via-white to-violet-50/40" data-testid="messaging-page">
      {/* ============ LEFT PANE ============ */}
      <aside
        className={`${active ? "hidden lg:flex" : "flex"} w-full lg:w-80 border-r border-slate-200 bg-white/85 backdrop-blur flex-col shrink-0`}
        data-testid="msg-sidebar"
      >
        <div className="p-4 border-b border-slate-200/80 bg-white">
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-[Outfit] font-bold text-slate-900 text-lg tracking-tight">Chats</h2>
            <div className="flex items-center gap-1">
              <Button variant="ghost" size="sm" className="h-8 w-8 p-0 rounded-lg hover:bg-violet-50 hover:text-violet-700" onClick={() => setNewDmOpen(true)} title="New direct message" data-testid="new-dm-btn"><Users className="w-4 h-4" /></Button>
              <Button variant="ghost" size="sm" className="h-8 w-8 p-0 rounded-lg hover:bg-violet-50 hover:text-violet-700" onClick={() => setNewChannelOpen(true)} title="New channel" data-testid="new-channel-btn"><Plus className="w-4 h-4" /></Button>
            </div>
          </div>
          <div className="relative">
            <Search className="absolute left-3 top-2.5 w-3.5 h-3.5 text-slate-400" />
            <Input
              value={search}
              onChange={(e) => { setSearch(e.target.value); setShowSearch(!!e.target.value); runSearch(e.target.value); }}
              placeholder="Search chats & messages"
              className="pl-9 h-9 text-sm bg-slate-50 border-slate-200 rounded-lg focus-visible:ring-violet-300"
              data-testid="messaging-search"
            />
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-2 space-y-1">
          {showSearch && searchResults.length > 0 && (
            <div className="mb-3">
              <div className="text-[10px] uppercase tracking-wider font-semibold text-slate-400 px-2 mb-1">Messages · {searchResults.length}</div>
              {searchResults.map(r => (
                <button key={r.id} className="w-full text-left px-2 py-1.5 rounded hover:bg-slate-100" onClick={() => {
                  const c = channels.find(cc => cc.id === r.channel_id); if (c) setActive(c);
                  setShowSearch(false); setSearch("");
                }}>
                  <div className="text-xs text-slate-500">#{r.channel_name}</div>
                  <div className="text-xs text-slate-800 truncate">{r.text}</div>
                </button>
              ))}
            </div>
          )}
          {sidebarSections.map((s) => {
            const isCollapsed = collapsed[s.key];
            return (
              <div key={s.key}>
                <button
                  onClick={() => setCollapsed(p => ({ ...p, [s.key]: !p[s.key] }))}
                  className="w-full flex items-center gap-1 px-2 py-1 text-[10px] uppercase tracking-wider font-semibold text-slate-400 hover:text-slate-700"
                >
                  {isCollapsed ? <ChevronRight className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />} {s.label}
                  <span className="ml-1 text-slate-300">· {s.items.length}</span>
                </button>
                {!isCollapsed && (
                  <div className="space-y-0.5">
                    {s.items.length === 0 ? (
                      s.key === "dm" ? <div className="px-3 py-1 text-[11px] text-slate-400">No direct messages yet. <button className="text-cyan-600 hover:underline" onClick={() => setNewDmOpen(true)}>Start one</button></div> : null
                    ) : (
                      s.items.map(ch => <ChannelRow key={ch.id} ch={ch} active={active?.id === ch.id} onClick={() => setActive(ch)} presence={presence} emps={emps} />)
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </aside>

      {/* ============ MAIN PANE ============ */}
      <section className={`${active ? "flex" : "hidden lg:flex"} flex-1 flex-col min-w-0`}>
        {!active ? (
          <div className="flex-1 flex items-center justify-center text-center">
            <div>
              <div className="w-16 h-16 mx-auto bg-slate-100 rounded-full flex items-center justify-center mb-4"><Hash className="w-7 h-7 text-slate-400" /></div>
              <h3 className="font-semibold text-slate-700">Pick a conversation</h3>
              <p className="text-sm text-slate-500 mt-1">Chat with your team across departments, projects, and direct messages.</p>
            </div>
          </div>
        ) : (
          <>
            {/* Header */}
            <div className="h-14 px-3 sm:px-5 border-b border-slate-200 bg-white flex items-center justify-between shrink-0 gap-2">
              <button onClick={() => setActive(null)} className="lg:hidden w-8 h-8 flex items-center justify-center rounded-md hover:bg-slate-100 shrink-0" data-testid="msg-back" aria-label="Back to chat list">
                <ChevronLeft className="w-5 h-5 text-slate-600" />
              </button>
              <div className="flex items-center gap-3 min-w-0 flex-1">
                {active.type === "dm" ? (
                  <UserAvatar emp={emps.find(e => e.id === (active.members || []).find(m => m !== myEmpId))} size={36} presence={(() => {
                    const peer = (active.members || []).find(m => m !== myEmpId);
                    const pe = emps.find(e => e.id === peer);
                    return presence[pe?.user_id || peer]?.status || "offline";
                  })()} />
                ) : (
                  <div className="w-9 h-9 rounded-lg bg-slate-100 flex items-center justify-center">{typeIcon(active.type, active)}</div>
                )}
                <div className="min-w-0">
                  <div className="font-semibold text-slate-900 truncate flex items-center gap-2">
                    {active.type === "dm" ? emps.find(e => e.id === (active.members || []).find(m => m !== myEmpId))?.full_name : active.name}
                    {active.type === "private" && <Lock className="w-3.5 h-3.5 text-slate-400" />}
                    {active.auto_managed && <Badge variant="outline" className="text-[9px] uppercase">Auto</Badge>}
                  </div>
                  <div className="text-xs text-slate-500 truncate">
                    {active.type === "dm"
                      ? (() => {
                          const peer = (active.members || []).find(m => m !== myEmpId);
                          const pe = emps.find(e => e.id === peer);
                          const st = presence[pe?.user_id || peer]?.status || "offline";
                          return st.charAt(0).toUpperCase() + st.slice(1);
                        })()
                      : `${active.member_count} member${active.member_count === 1 ? "" : "s"} · ${active.description || active.type}`}
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-1">
                <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={togglePin} title={active.pinned ? "Unpin" : "Pin"} data-testid="chan-pin"><Pin className={`w-4 h-4 ${active.pinned ? "text-cyan-600 fill-cyan-600" : "text-slate-500"}`} /></Button>
                <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={toggleMute} title={active.muted ? "Unmute" : "Mute"}>{active.muted ? <BellOff className="w-4 h-4 text-amber-500" /> : <Bell className="w-4 h-4 text-slate-500" />}</Button>
                {active.type !== "dm" && (
                  <Button variant="ghost" size="sm" className="h-8 px-2 text-xs" onClick={() => setMemberMgrOpen(true)} data-testid="chan-members"><Users className="w-4 h-4 mr-1" />{active.member_count}</Button>
                )}
                <Button variant="ghost" size="sm" className={`h-8 w-8 p-0 ${showDetails ? "bg-slate-100" : ""}`} onClick={() => setShowDetails(s => !s)} title={showDetails ? "Hide details" : "Show details"} data-testid="chan-details">
                  <MoreHorizontal className="w-4 h-4 text-slate-500" />
                </Button>
              </div>
            </div>

            {/* Messages */}
            <div ref={listRef} className="flex-1 overflow-y-auto p-5 space-y-0.5" data-testid="messages-list">
              {msgsWithDays.length === 0 ? (
                <div className="h-full flex items-center justify-center text-center">
                  <div>
                    <div className="text-5xl mb-2">💬</div>
                    <div className="font-medium text-slate-700">No messages yet</div>
                    <div className="text-xs text-slate-500 mt-1">Be the first to say hi.</div>
                  </div>
                </div>
              ) : (
                msgsWithDays.map((item, idx) => {
                  if (item.__day) {
                    return <div key={`d-${idx}`} className="flex items-center gap-2 my-4"><div className="flex-1 border-t border-slate-200" /><span className="text-[10px] uppercase tracking-wider font-semibold text-slate-400 bg-slate-50 px-2">{dayLabel(item.__day)}</span><div className="flex-1 border-t border-slate-200" /></div>;
                  }
                  const prev = msgsWithDays[idx - 1];
                  const prevSender = prev && !prev.__day ? prev.sender_id : null;
                  return <MessageBubble
                    key={item.id} m={item} mine={item.sender_id === myEmpId}
                    sender={item.sender || emps.find(e => e.id === item.sender_id)}
                    prevSender={prevSender} compact={true}
                    onReact={reactTo} onReply={setReplyTo} onDelete={deleteMsg}
                  />;
                })
              )}
            </div>

            {/* Composer — rich, Slack/Teams style */}
            {!(active.read_only_for_members && user?.role !== "super_admin" && user?.role !== "hr_admin") && (
              <div className="border-t border-slate-200 bg-white p-3 sm:p-4 shrink-0">
                {replyTo && (
                  <div className="mb-2 flex items-center gap-2 px-3 py-1.5 bg-violet-50 border border-violet-100 rounded-md">
                    <Reply className="w-3.5 h-3.5 text-violet-600" />
                    <div className="flex-1 min-w-0">
                      <div className="text-[10px] font-semibold text-violet-700">Replying to {replyTo.sender?.full_name || "message"}</div>
                      <div className="text-xs text-slate-700 truncate">{replyTo.text}</div>
                    </div>
                    <button onClick={() => setReplyTo(null)} className="p-1 hover:bg-violet-100 rounded" data-testid="cancel-reply"><X className="w-3 h-3" /></button>
                  </div>
                )}
                <div className="rounded-xl border border-slate-200 bg-white focus-within:border-violet-300 focus-within:ring-2 focus-within:ring-violet-100 transition">
                  <Textarea
                    value={text}
                    onChange={(e) => setText(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
                    placeholder={`Message ${active.type === "dm" ? emps.find(e => e.id === (active.members || []).find(m => m !== myEmpId))?.full_name || "" : "#" + (active.slug || active.name)}`}
                    rows={2}
                    className="resize-none min-h-[44px] max-h-[140px] border-0 focus-visible:ring-0 bg-transparent"
                    data-testid="msg-composer"
                  />
                  <div className="flex items-center justify-between px-2 py-1.5 border-t border-slate-100">
                    <div className="flex items-center gap-0.5">
                      <button title="Add attachment" onClick={() => toast.info("Choose attachment from menu")} className="w-8 h-8 rounded-md hover:bg-slate-100 flex items-center justify-center text-slate-500 hover:text-violet-700" data-testid="composer-attach">
                        <Plus className="w-4 h-4" />
                      </button>
                      <button title="Upload file" onClick={() => toast.info("Drag-and-drop or paste files into the composer")} className="w-8 h-8 rounded-md hover:bg-slate-100 flex items-center justify-center text-slate-500 hover:text-violet-700">
                        <Paperclip className="w-4 h-4" />
                      </button>
                      <button title="Emoji" className="w-8 h-8 rounded-md hover:bg-slate-100 flex items-center justify-center text-slate-500 hover:text-violet-700">
                        <Smile className="w-4 h-4" />
                      </button>
                      <button title="Mention someone" className="w-8 h-8 rounded-md hover:bg-slate-100 flex items-center justify-center text-slate-500 hover:text-violet-700 text-sm font-bold">
                        @
                      </button>
                      <button title="Format" className="w-8 h-8 rounded-md hover:bg-slate-100 flex items-center justify-center text-slate-500 hover:text-violet-700 text-[10px] font-bold">
                        Aa
                      </button>
                      <button title="Start meeting" className="w-8 h-8 rounded-md hover:bg-slate-100 flex items-center justify-center text-slate-500 hover:text-violet-700">
                        <Video className="w-4 h-4" />
                      </button>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-[10px] text-slate-400 hidden sm:inline">Press Enter to send · Shift+Enter for new line</span>
                      <button onClick={send} disabled={!text.trim()} className="qh-btn btn-primary qh-btn-sm" data-testid="send-msg">
                        <Send className="w-3.5 h-3.5" />
                        Send
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}
            {active.read_only_for_members && user?.role !== "super_admin" && user?.role !== "hr_admin" && (
              <div className="border-t border-slate-200 bg-slate-50 p-4 text-center text-xs text-slate-500">This channel is read-only. Only admins can post announcements.</div>
            )}
          </>
        )}
      </section>

      {/* ============ RIGHT DETAILS PANE ============ */}
      {active && showDetails && (
        <aside className="hidden xl:flex w-80 border-l border-slate-200 bg-white flex-col shrink-0" data-testid="msg-details-pane">
          <div className="h-14 px-4 border-b border-slate-200 flex items-center justify-between shrink-0">
            <div className="text-sm font-bold font-[Outfit] text-slate-900">Details</div>
            <button onClick={() => setShowDetails(false)} className="w-7 h-7 flex items-center justify-center rounded-md hover:bg-slate-100">
              <X className="w-4 h-4 text-slate-500" />
            </button>
          </div>
          <div className="flex-1 overflow-y-auto p-4 space-y-5">
            {/* Channel info */}
            <div>
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-xl qh-grad-bg text-white flex items-center justify-center">
                  {active.type === "dm" ? <Users className="w-5 h-5" /> : typeIcon(active.type, active) || <Hash className="w-5 h-5" />}
                </div>
                <div className="min-w-0">
                  <div className="font-bold font-[Outfit] text-slate-900 truncate">
                    {active.type === "dm" ? emps.find(e => e.id === (active.members || []).find(m => m !== myEmpId))?.full_name : active.name}
                  </div>
                  <div className="text-[11px] text-slate-500 capitalize">{active.type === "dm" ? "Direct message" : `${active.type} channel`}</div>
                </div>
              </div>
              {active.description && <p className="text-[12.5px] text-slate-600 mt-3 leading-relaxed">{active.description}</p>}
            </div>

            {/* Members */}
            {active.type !== "dm" && (
              <div>
                <div className="flex items-center justify-between mb-2">
                  <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500">Members · {activeMembers.length}</div>
                  {canEditMembers && (
                    <button onClick={() => setMemberMgrOpen(true)} className="text-[11px] font-semibold text-violet-700 hover:underline" data-testid="details-add-member">Manage</button>
                  )}
                </div>
                <div className="space-y-1.5 max-h-56 overflow-y-auto">
                  {activeMembers.slice(0, 12).map((m) => (
                    <div key={m.id} className="flex items-center gap-2.5 py-1">
                      <UserAvatar emp={m} size={28} presence={presence[m.user_id || m.id]?.status} />
                      <div className="flex-1 min-w-0">
                        <div className="text-[12.5px] font-semibold text-slate-900 truncate">{m.full_name}</div>
                        <div className="text-[10px] text-slate-500 truncate">{m.designation || m.role || "Member"}</div>
                      </div>
                    </div>
                  ))}
                  {activeMembers.length > 12 && <div className="text-[11px] text-slate-400 text-center py-1">+{activeMembers.length - 12} more</div>}
                </div>
              </div>
            )}

            {/* Shared files */}
            <div>
              <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-2">Shared Files</div>
              {(() => {
                const files = msgs.flatMap(m => (m.attachments || []).map(a => ({ ...a, msg_id: m.id, sender: m.sender_id })));
                if (files.length === 0) {
                  return <div className="text-[11px] text-slate-400 py-2">Files shared in this chat appear here.</div>;
                }
                return (
                  <div className="space-y-1.5">
                    {files.slice(-5).reverse().map((f, i) => (
                      <div key={i} className="flex items-center gap-2 p-2 rounded-lg bg-slate-50 hover:bg-slate-100 transition">
                        <Paperclip className="w-3.5 h-3.5 text-slate-500 shrink-0" />
                        <div className="text-[12px] text-slate-800 truncate flex-1">{f.name || "attachment"}</div>
                      </div>
                    ))}
                  </div>
                );
              })()}
            </div>

            {/* Quick actions */}
            <div>
              <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-2">Quick actions</div>
              <div className="grid grid-cols-2 gap-2">
                <button onClick={togglePin} className="px-3 py-2 rounded-lg bg-slate-50 hover:bg-violet-50 text-[12px] font-semibold text-slate-700 hover:text-violet-700 border border-slate-100 hover:border-violet-200 transition flex items-center justify-center gap-1.5">
                  <Pin className="w-3 h-3" /> {active.pinned ? "Unpin" : "Pin"}
                </button>
                <button onClick={toggleMute} className="px-3 py-2 rounded-lg bg-slate-50 hover:bg-violet-50 text-[12px] font-semibold text-slate-700 hover:text-violet-700 border border-slate-100 hover:border-violet-200 transition flex items-center justify-center gap-1.5">
                  {active.muted ? <BellOff className="w-3 h-3" /> : <Bell className="w-3 h-3" />} {active.muted ? "Unmute" : "Mute"}
                </button>
              </div>
            </div>

            <div className="text-[10px] text-slate-400 text-center pt-2 border-t border-slate-100">
              Channel ID: <span className="font-mono">{active.id?.slice(0, 8)}…</span>
            </div>
          </div>
        </aside>
      )}

      {/* ============ NEW CHANNEL DIALOG ============ */}
      <Dialog open={newChannelOpen} onOpenChange={setNewChannelOpen}>
        <DialogContent className="max-w-lg" data-testid="new-channel-dialog">
          <DialogHeader>
            <DialogTitle>Create a channel</DialogTitle>
            <DialogDescription>Channels are for group conversations. Public channels are visible to everyone, private channels only to invited members.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <label className="text-xs font-semibold text-slate-600 uppercase">Name</label>
              <Input value={newForm.name} onChange={(e) => setNewForm({ ...newForm, name: e.target.value })} placeholder="e.g. product-launch" className="mt-1.5" data-testid="nc-name" />
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-600 uppercase">Description</label>
              <Input value={newForm.description} onChange={(e) => setNewForm({ ...newForm, description: e.target.value })} placeholder="What's this channel for?" className="mt-1.5" />
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-600 uppercase">Visibility</label>
              <div className="grid grid-cols-2 gap-2 mt-1.5">
                <button onClick={() => setNewForm({ ...newForm, type: "public" })} className={`p-3 rounded-lg border text-left ${newForm.type === "public" ? "border-cyan-500 bg-cyan-50" : "border-slate-200"}`}>
                  <div className="flex items-center gap-2"><Globe2 className="w-4 h-4 text-cyan-600" /><span className="text-sm font-semibold">Public</span></div>
                  <div className="text-[11px] text-slate-500 mt-0.5">Anyone can join & see history</div>
                </button>
                <button onClick={() => setNewForm({ ...newForm, type: "private" })} className={`p-3 rounded-lg border text-left ${newForm.type === "private" ? "border-cyan-500 bg-cyan-50" : "border-slate-200"}`} data-testid="nc-private">
                  <div className="flex items-center gap-2"><Lock className="w-4 h-4 text-slate-600" /><span className="text-sm font-semibold">Private</span></div>
                  <div className="text-[11px] text-slate-500 mt-0.5">Only invited members</div>
                </button>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setNewChannelOpen(false)}>Cancel</Button>
            <Button className="bg-slate-900 hover:bg-slate-800" onClick={createChannel} disabled={!newForm.name} data-testid="nc-create">Create</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ============ NEW DM DIALOG ============ */}
      <Dialog open={newDmOpen} onOpenChange={setNewDmOpen}>
        <DialogContent className="max-w-md" data-testid="new-dm-dialog">
          <DialogHeader>
            <DialogTitle>Start a direct message</DialogTitle>
            <DialogDescription>Pick a teammate to open a one-on-one chat.</DialogDescription>
          </DialogHeader>
          <div className="max-h-80 overflow-y-auto divide-y divide-slate-100">
            {emps.filter(e => e.id !== myEmpId).map(e => (
              <button key={e.id} onClick={() => startDM(e.id)} className="w-full flex items-center gap-3 p-2 hover:bg-slate-50 rounded" data-testid={`dm-pick-${e.id}`}>
                <UserAvatar emp={e} size={34} presence={presence[e.user_id || e.id]?.status || "offline"} />
                <div className="text-left flex-1 min-w-0">
                  <div className="text-sm text-slate-900 truncate">{e.full_name}</div>
                  <div className="text-xs text-slate-500 truncate">{e.designation || e.role}</div>
                </div>
              </button>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      {/* ============ MEMBER MANAGER ============ */}
      <Dialog open={memberMgrOpen} onOpenChange={setMemberMgrOpen}>
        <DialogContent className="max-w-lg" data-testid="member-mgr-dialog">
          <DialogHeader>
            <DialogTitle>Members of {active?.name}</DialogTitle>
            <DialogDescription>{canEditMembers ? "Add or remove people from this channel." : active?.auto_managed ? "This channel's membership is auto-managed from department/project data." : "You don't have permission to change members."}</DialogDescription>
          </DialogHeader>
          <div className="max-h-80 overflow-y-auto space-y-1">
            {activeMembers.map(m => (
              <div key={m.id} className="flex items-center gap-3 p-2 hover:bg-slate-50 rounded">
                <UserAvatar emp={m} size={30} />
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-slate-900 truncate">{m.full_name}</div>
                  <div className="text-xs text-slate-500 truncate">{m.designation}</div>
                </div>
                {canEditMembers && m.id !== myEmpId && (
                  <Button variant="ghost" size="sm" onClick={() => updateMembers([], [m.id])} className="text-red-500 h-7" data-testid={`remove-member-${m.id}`}><X className="w-4 h-4" /></Button>
                )}
              </div>
            ))}
          </div>
          {canEditMembers && (
            <div>
              <div className="text-xs font-semibold text-slate-500 uppercase mb-1">Add people</div>
              <div className="max-h-40 overflow-y-auto space-y-1">
                {emps.filter(e => !(active?.members || []).includes(e.id)).slice(0, 50).map(e => (
                  <button key={e.id} onClick={() => updateMembers([e.id], [])} className="w-full flex items-center gap-3 p-2 hover:bg-slate-50 rounded text-left">
                    <UserAvatar emp={e} size={28} />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm text-slate-900 truncate">{e.full_name}</div>
                      <div className="text-xs text-slate-500 truncate">{e.designation}</div>
                    </div>
                    <Plus className="w-4 h-4 text-slate-400" />
                  </button>
                ))}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
